import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { SubGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useEquipmentTransferMutation } from '@/app/services/detailsEquipmentApi';
import {
  useSearchEquipmentAppropriationQuery,
  useSearchEquipmentClassificationQuery,
} from '@/app/services/financialClassificationApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import {
  cpfOrCpnjFromBack,
  formatCurrency,
  formatHandleCPFAndCNPJ,
  isSupplierCodeValid,
} from '@/app/utils/numberUtils';
import FormFieldDate from '@/components/common/DateField';
import DrawerButtons from '@/components/common/DrawerButtons';
import { usePeriod } from '@/context/PeriodContext';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldQuantity from '../baseForms/FormFieldQuantity';
import FormFieldSelect from '../baseForms/FormFieldSelect';

interface NewEquipmentTransferProps {
  selectedItem: TransferDetailBase;
  setDrawerOpen: (open: boolean) => void;
  selectedFinancialSubGroup: SubGroup;
}

export default function NewEquipmentTransfer(props: NewEquipmentTransferProps) {
  const { t, i18n } = useTranslation(['equipment', 'common']);
  const theme = useTheme();
  const { selectedPeriod } = usePeriod();
  const { selectedItem, setDrawerOpen, selectedFinancialSubGroup } = props;

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectedItem?.newDetail.value);
  const [justification, setJustification] = useState<string>('');
  const [classification, setClassification] = useState<string>('');
  const [appropriation, setAppropriation] = useState<string>('');
  const [description, setDescription] = useState<string>(
    selectedItem?.newDetail.description != null ? selectedItem?.newDetail.description : '',
  );
  const [quantity, setQuantity] = useState<number | null>(null);

  const [supplier, setSupplier] = useState<string>(
    selectedItem?.newDetail.supplier != null ? selectedItem?.newDetail.supplier : '',
  );

  const [supplierCode, setSupplierCode] = useState<string>('');

  const [invoiceCode, setInvoiceCode] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const [acquisition, setAcquisitionDate] = useState<Dayjs | null>(null);
  const [expenseDate, setExpenseDate] = useState<string | null>(null);

  const [transfer] = useEquipmentTransferMutation();

  const handleSubmit = async () => {
    if (!selectedItem) return;
    setDrawerOpen(false);

    await toast.promise(
      transfer({
        id: selectedItem.id,
        originFinancialGroupCode: selectedItem.originFinancialGroupCode as string,
        financialSubGroupId: selectedFinancialSubGroup.id as number,
        period: selectedPeriod?.date as string,
        newDetail: {
          value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
          description: description || '',
          quantity: quantity || 0,
          supplier: supplier || '',
          supplierCode: cpfOrCpnjFromBack(supplierCode),
          invoiceCode: invoiceCode || '',
          classification: classification || '',
          appropriation: appropriation || '',
          acquisition: formatDateForBackend(acquisition as Dayjs) as string,
          justify: justification || '',
          expenseDate: formatDateForBackend(expenseDate as string),
        },
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:success.transfer') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  const { data: financialClassification } = useSearchEquipmentClassificationQuery();
  const { data: financialApropriation } = useSearchEquipmentAppropriationQuery();

  const handleSupplierCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatHandleCPFAndCNPJ(e.target.value);
    setSupplierCode(formatted);
  };
  const supplierCodeIsValid = isSupplierCodeValid(supplierCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(80vh - 220px)',
        maxHeight: 'calc(80vh - 220px)',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            gap: 2,
            flexWrap: 'wrap',
          }}
        />
        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField label={t('common:description') as string} value={description} onChange={handleChange} />

          <FormFieldQuantity label={t('common:quantity')} value={quantity} onChange={setQuantity} />

          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />

          <FormField
            label={t('common:supplier') as string}
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
            }}
          />

          <FormField
            label={t('common:cnpj') as string}
            value={supplierCode}
            onChange={handleSupplierCode}
            error={!supplierCodeIsValid}
          />

          <FormField
            label={t('common:invoice.number') as string}
            value={invoiceCode}
            onChange={(e) => {
              setInvoiceCode(e.target.value);
            }}
            error={!supplierCodeIsValid}
          />

          <FormFieldSelect
            label={t('classification.mct')}
            value={classification}
            onChange={setClassification}
            options={financialClassification ?? []}
            placeholder={t('select.classification') as string}
          />

          <FormFieldSelect
            label={t('appropriation.type')}
            value={appropriation}
            onChange={setAppropriation}
            options={financialApropriation ?? []}
            placeholder={t('select.apropriation') as string}
          />

          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <Typography variant="fieldHeader" width={theme.spacing(15)}>
                  {t('acquisition.date')}
                </Typography>
                <Stack direction="row" sx={{ width: '30%', ml: 7 }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={acquisition}
                    onChange={(newValue: Dayjs | null) => setAcquisitionDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    slots={{ openPickerIcon: () => null }}
                  />
                </Stack>
              </Stack>
            </LocalizationProvider>
          </Box>

          <FormFieldDate label={t('common:expenseDate')} value={expenseDate as string} onChange={setExpenseDate} />

          <FormFieldLong
            label={t('common:justification')}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons
          onSave={() => {
            handleSubmit();
          }}
          onCancel={handleCancel}
          labelSave={t('common:transfer') as string}
          disabled={!supplierCodeIsValid || !expenseDate}
        />
      </Box>
    </Box>
  );
}
